import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getCbtToken } from "shared/cbt/cbt";

import {
  setBookingFormFromAcquisitionParams,
  setBookingFormValue,
} from "../../data/actions/bookingForm";
import { setCbtValue } from "../../data/actions/cbt";
import { AppState } from "../../data/reducers";
import { getCbtGuestsInDynamicFormShowingSearchForm } from "../../lib/bookingFormHelpers";
import { getArrayRefsFromFlatRefs } from "../../lib/helperFunctions";
import { useUserDetail } from "../userDetail";
import { enhanceDataWithManagerApproval } from "./enhanceDataWithManagerApproval";
import { fetchBookingFormFromAcquisitionParams } from "./fetchBookingFormFromAcquisitionParams";
import { handleCbtManagerApproval } from "./handleCbtManagerApproval";
import { prefillFields } from "./prefillFields";

let cbtCached;

export interface BookingFormProps {
  refs: string;
  fullPrice: string;
  isFlight?: boolean;
  isHotel?: boolean;
  policyFailed?: boolean;
}

export function useBookingForm({
  refs,
  fullPrice,
  query,
  intl,
  isFlight,
  isHotel,
}): {
  bookingFormSections: any[];
  bookingFormFields: any;
  isBookingFormReady: boolean;
} {
  const [isBookingFormReady, setIsBookingFormReady] = useState(false);

  const userDetail = useUserDetail();

  const {
    bookingFormSections,
    bookingFormFields,
    customerUsername,
    cbt,
    cbtApiUrl,
    defaultCountry,
    promoCode,
  } = useSelector((state: AppState) => ({
    bookingFormSections: state.bookingForm.bookingFormSections,
    bookingFormFields: state.bookingForm.bookingFormFields,
    cbt: state.cbt,
    customerUsername: state.user.email,
    cbtApiUrl:
      state.storage.frontendSettings?.dealerCorporateSettings?.cbtApiUrl,
    defaultCountry:
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry,
    promoCode: state.bookingForm.promoCode,
  }));
  const { user, frontendSettings } = useSelector((state: AppState) => ({
    frontendSettings: state.storage.frontendSettings,
    user: state.user,
  }));

  const dispatch = useDispatch();
  const cbtFrozen = JSON.stringify(cbt);

  useEffect(() => {
    cbtCached = cbt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbtFrozen]);

  useEffect(() => {
    async function prepareBookingForm() {
      setIsBookingFormReady(false);

      const {
        acquisitionParamsBookingForm,
        sections,
        fields,
        codeBook,
        ticketingCombinations,
        paymentFormMethods,
        alternativeCurrencyPaymentFormMethods,
        paymentFormMethodForms,
      } = await fetchBookingFormFromAcquisitionParams({
        refs: getArrayRefsFromFlatRefs(refs),
        fullPrice,
        cbt: cbtCached || cbt,
        query,
        cbtApiUrl,
        customerUsername,
        intl,
        isFlight,
        isHotel,
        defaultCountry,
        promoCode,
      });

      const cbtToken = await getCbtToken(
        user,
        frontendSettings,
        customerUsername
      );

      const managedTravellers = await handleCbtManagerApproval(
        cbtCached || cbt,
        query?.policyResolvedId,
        {
          cbtApiUrl,
          customerUsername,
          cbtToken,
        }
      );

      dispatch(setBookingFormValue("managedTravellers", managedTravellers));

      const prefilledFields = prefillFields({
        fields,
        userDetail,
        cbtTravelersIds: query?.userIds || cbt.passengersForm.travellers,
        cbt: cbtCached || cbt,
        previousBookingFormFields: bookingFormFields,
        codeBook,
      });

      const cbtGuestsInDynamicFormShowingSearchForm = getCbtGuestsInDynamicFormShowingSearchForm(
        { sections, cbt: cbtCached || cbt }
      );

      dispatch(setCbtValue("guestsUsedInDynamicForm", null));

      dispatch(
        setBookingFormFromAcquisitionParams({
          ...enhanceDataWithManagerApproval({
            sections,
            fields: prefilledFields,
            managedTravellers,
            cbt: cbtCached || cbt,
            intl,
          }),
          acquisitionParamsBookingForm,
          codeBook,
          ticketingCombinations,
          cbtGuestsInDynamicFormShowingSearchForm,
          paymentFormMethods,
          alternativeCurrencyPaymentFormMethods,
          paymentFormMethodForms,
        })
      );

      const isBillingPrefilled = Object.values(prefilledFields).find(
        (el) =>
          el.name.includes("billing") && el.type !== "select" && !!el.value
      );

      if (isBillingPrefilled) {
        dispatch(
          setBookingFormValue(
            "bookingFormSections",
            sections.map((bookingFormSectionInside) => {
              if (bookingFormSectionInside.code === "billing") {
                return { ...bookingFormSectionInside, isCollapsed: false };
              }

              return bookingFormSectionInside;
            })
          )
        );
      }

      setIsBookingFormReady(true);
    }

    prepareBookingForm();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs, isFlight, isHotel]);

  return {
    bookingFormSections,
    bookingFormFields,
    isBookingFormReady,
  };
}
